import { useState, useRef } from 'react';
// The server's own allowlist (pen test AD25996), so the picker offers only what will be
// taken and a refusal by name is said here before anything is sent.
import { acceptAttribute, uploadRefusal, UPLOAD_KINDS } from '../../../../src/lib/fileSafety.js';

const DOCUMENT_ACCEPT = acceptAttribute('document');

/**
 * Reusable file upload component with drag-and-drop.
 *
 * Props:
 *   onUpload(result)    — called with { url, key, filename, size, content_type } after a successful upload
 *   onClear()           — optional, called when the user removes the uploaded file
 *   accept              — optional MIME types / extensions string (e.g. "application/pdf,.docx");
 *                         defaults to everything /api/uploads takes
 *   label               — optional label override (default "Drag file here or click to browse")
 *   value               — optional current filename to show as already-uploaded state
 *   resetAfterUpload    — if true, resets to drop-zone after each upload (for multi-file accumulation)
 */
export default function FileUpload({ onUpload, onClear, accept = DOCUMENT_ACCEPT, label, value, resetAfterUpload }) {
  const [dragging,  setDragging]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded,  setUploaded]  = useState(value || null); // filename string once done
  const [error,     setError]     = useState('');
  const inputRef = useRef(null);

  // Uploads travel as base64 inside a JSON body (~33% inflation), and Workers
  // caps request bodies at 100 MB, so the server's 70 MB cap is also the real
  // transport ceiling. Reject early with a clear message instead of a mid-upload 4xx.
  const MAX_UPLOAD_MB = UPLOAD_KINDS.document.maxMb;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`"${file.name}" is larger than ${MAX_UPLOAD_MB} MB, which is the most Ops takes.`);
      return;
    }
    // A dropped file skips the picker's filter, so the name is checked here too. The
    // server checks the content as well and its reason is shown the same way.
    const refused = uploadRefusal(file.name, file.type, 'document');
    if (refused) { setError(refused); return; }
    setUploading(true);
    setError('');
    try {
      // Read as base64 data URL, strip the "data:...;base64," prefix
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch('/api/uploads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/octet-stream', data: base64 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Upload failed (${res.status})`);
      }
      const result = await res.json();
      if (!resetAfterUpload) setUploaded(result.filename);
      onUpload(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };

  const handleClear = () => {
    setUploaded(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
    onClear?.();
  };

  if (uploaded) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
        </svg>
        <span className="text-[9px] font-semibold text-emerald-700 flex-1 truncate">{uploaded}</span>
        <button type="button" onClick={handleClear}
          className="text-emerald-400 hover:text-red-500 font-bold text-[11px] leading-none flex-shrink-0">×</button>
      </div>
    );
  }

  return (
    <div>
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
          dragging   ? 'border-gold bg-gold/5'
          : uploading ? 'border-gray-300 bg-gray-50 cursor-wait'
          : 'border-gray-300 hover:border-gold/60 hover:bg-gray-50'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-1.5">
            <svg className="w-5 h-5 text-gray-400 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            <span className="text-[9px] text-gray-400">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span className="text-[9px] text-gray-400">
              {label || 'Drag file here or click to browse'}
            </span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleChange}
        />
      </div>
      {error && <div role="alert" className="mt-1 px-2 py-1 text-[9px] text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
    </div>
  );
}
